import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { reportError, addBreadcrumb } from '../../config/sentry';
import FeedbackDialog from './FeedbackDialog';
import { logErrorToAnalytics } from '../../utils/errorTracking';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'page' | 'component' | 'critical';
  context?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  showFeedbackDialog: boolean;
}

/**
 * Enhanced Error Boundary with Sentry integration
 * Provides graceful error handling and user-friendly fallbacks
 */
export class ErrorBoundary extends Component<Props, State> {
  private retryCount: number = 0;
  private readonly maxRetries: number = 2;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      showFeedbackDialog: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: Math.random().toString(36).substr(2, 9),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { level = 'component', context, onError } = this.props;

    // Add breadcrumb for debugging context
    addBreadcrumb(
      `Error boundary caught error in ${context || 'unknown component'}`,
      'error-boundary',
      'error',
      {
        level,
        componentStack: errorInfo.componentStack,
        retryCount: this.retryCount,
      }
    );

    // Report to Sentry with enhanced context
    reportError(error, {
      errorBoundary: {
        level,
        context: context || 'unknown',
        componentStack: errorInfo.componentStack,
        retryCount: this.retryCount,
        props: this.props,
        timestamp: new Date().toISOString(),
      },
      errorInfo,
    });

    // Also report to Google Analytics
    logErrorToAnalytics(error, errorInfo);

    // Store error info in state
    this.setState({
      errorInfo,
      errorId: Sentry.lastEventId() || this.state.errorId,
    });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Boundary Caught Error');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Context:', context);
      console.error('Level:', level);
      console.groupEnd();
    }
  }

  handleRetry = (): void => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;

      addBreadcrumb(`User initiated retry attempt ${this.retryCount}`, 'user-action', 'info');

      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: null,
      });
    }
  };

  handleReload = (): void => {
    addBreadcrumb('User initiated page reload', 'user-action', 'info');
    window.location.reload();
  };

  handleReportIssue = (): void => {
    this.setState({ showFeedbackDialog: true });
  };

  handleCloseFeedback = (): void => {
    this.setState({ showFeedbackDialog: false });
  };

  render(): ReactNode {
    const { hasError, error, errorId, showFeedbackDialog } = this.state;
    const { children, fallback, level = 'component', context } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return (
          <>
            {fallback}
            <FeedbackDialog
              isOpen={showFeedbackDialog}
              onClose={this.handleCloseFeedback}
              errorId={errorId || undefined}
              errorMessage={error?.message}
              technicalContext={{
                sentryErrorId: errorId || undefined,
                userAgent: navigator.userAgent,
                url: window.location.href,
                timestamp: new Date().toISOString(),
                errorMessage: error?.message,
                errorStack: error?.stack,
              }}
            />
          </>
        );
      }

      // Render appropriate error UI based on level
      return (
        <>
          {this.renderErrorUI(level, error, errorId, context)}
          <FeedbackDialog
            isOpen={showFeedbackDialog}
            onClose={this.handleCloseFeedback}
            errorId={errorId || undefined}
            errorMessage={error?.message}
            technicalContext={{
              sentryErrorId: errorId || undefined,
              userAgent: navigator.userAgent,
              url: window.location.href,
              timestamp: new Date().toISOString(),
              errorMessage: error?.message,
              errorStack: error?.stack,
            }}
          />
        </>
      );
    }

    return children;
  }

  private renderErrorUI(
    level: string,
    error: Error | null,
    errorId: string | null,
    context?: string
  ): ReactNode {
    const canRetry = this.retryCount < this.maxRetries;

    if (level === 'critical') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full text-center">
            <div className="text-red-500 mb-6">
              <svg
                className="mx-auto h-16 w-16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Application Error</h1>
            <p className="text-gray-600 mb-8">
              We're sorry, but something went wrong. The application has encountered an unexpected
              error.
            </p>
            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleReportIssue}
                className="w-full bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Report Issue
              </button>
              {errorId && <div className="text-sm text-gray-500 mt-4">Error ID: {errorId}</div>}
            </div>
          </div>
        </div>
      );
    }

    if (level === 'page') {
      return (
        <div className="min-h-96 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="text-orange-500 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Page Error</h2>
            <p className="text-gray-600 mb-6">
              This page is temporarily unavailable. We're working to fix the issue.
            </p>
            <div className="space-x-3">
              {canRetry && (
                <button
                  onClick={this.handleRetry}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
              )}
              <button
                onClick={() => window.history.back()}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={this.handleReportIssue}
                className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 transition-colors"
              >
                Report Issue
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Component level error
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
        <div className="flex items-center">
          <div className="text-red-500 mr-3">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800">Component Error</h3>
            <p className="text-sm text-red-700 mt-1">
              {context ? `The ${context} component` : 'This component'} failed to load.
            </p>
            <div className="mt-2 space-x-3">
              {canRetry && (
                <button
                  onClick={this.handleRetry}
                  className="text-sm text-red-600 hover:text-red-800 underline"
                >
                  Retry ({this.maxRetries - this.retryCount} left)
                </button>
              )}
              <button
                onClick={this.handleReportIssue}
                className="text-sm text-red-600 hover:text-red-800 underline"
              >
                Report Issue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Higher-order component for wrapping components with error boundaries
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WithErrorBoundaryComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundaryComponent.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithErrorBoundaryComponent;
}

/**
 * Sentry-integrated Error Boundary for easier usage
 */
export const SentryErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: ReactNode }) => <>{children}</>,
  {
    fallback: ({ error, resetError }) => (
      <div className="error-boundary error-boundary--sentry">
        <div className="error-boundary__container">
          <h2>Something went wrong</h2>
          <p>We've been notified about this error and will fix it soon.</p>
          <button onClick={resetError} className="btn btn--primary">
            Try Again
          </button>
        </div>
      </div>
    ),
    beforeCapture: (scope) => {
      scope.setTag('errorBoundary', 'sentry-wrapper');
    },
  }
);
